import { useState, type PointerEvent } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { NavLink, resolvePath, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, TablePropertiesIcon } from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import Count from '@/components/ui/Count.tsx'
import { Dot } from '@/assets/icons/dot.tsx'
import { useProjectStageStore } from '@/components/common/menu/lib/store/useProjectStageStore.tsx'
import { useNewIssueDialogStore } from '@/routes/projectStages/issues/lib/stores/useNewIssueDialogStore.ts'
import { NavigationItemT } from '@/lib/types/navigationTypes.ts'
import { useLicenceAllowsCreate } from '@/lib/licence/useStageLicence.ts'

/*
Floating bottom sheet — modelled on Linear's iOS "more" menu. Previously
used the shared DialogContent, which hard-codes a full-height mobile modal
(h-[calc(100svh-...)]), so the popup ate the whole screen with mostly
empty space around a short list. That felt heavy for "pick an issue type".

This version:
  - Floats above the bottom tab bar with small side margins (mx-3), rounded
    top+bottom, soft shadow.
  - Auto-sizes to its content, capped at ~70vh with internal scroll.
  - Tiny drag-handle pill on top (iOS convention).
  - Subtle dim overlay so the backdrop is present but not dominant.
  - Swipe down on the handle/title area dismisses it.

Exit animations hold their end frame (`fill-mode-forwards`). Radix Presence only sets `animation-fill-mode: forwards`
inside its `animationend` handler; on iOS the slide-out runs on the
compositor, so when the main thread is busy rendering the route we just
navigated to, the finished animation snapped the sheet back to fully visible
until JS caught up — a flash on cheap routes, a "reopened", untappable sheet
on slow ones.

Intentionally bypasses ui/Dialog.tsx's `DialogContent` wrapper to avoid
retrofitting the full-screen mobile modal into a bottom sheet globally.
*/

type PropsT = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function BottomTabBarIssuesSheet({ open, onOpenChange }: PropsT) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const stageIri = useProjectStageStore(s => s.iri)
  const links = useProjectStageStore(s => s.projectStageIssuesLinks)
  const issueTypes = useProjectStageStore(s => s.issueTypes)
  const setCurrIssueType = useProjectStageStore(s => s.setCurrIssueType)
  const setDialogOpened = useNewIssueDialogStore(s => s.updateDialogState)
  const setDraftIssueId = useNewIssueDialogStore(s => s.setDraftIssueId)
  const setKeepOpenOnNavigationTo = useNewIssueDialogStore(s => s.setKeepOpenOnNavigationTo)
  const licenceAllowsCreate = useLicenceAllowsCreate()

  const archivePath = stageIri
    ? `${stageIri}/archive?order=DESC&sort_by=CREATED_AT&view=ROWS`
    : null

  const handleClose = () => onOpenChange(false)
  const drag = useSwipeDownToDismiss(open, handleClose)

  // Shortcut: jump to that type's list and pop the new-issue dialog over it.
  // The dialog host lives in ProjectsRoot and stays mounted across the stage
  // screens except the issue detail (see hostsStageNewIssueDialog), so the
  // Zustand `dialogOpen` flag opens it on the list we navigate to. Navigating
  // is part of the shortcut: the user lands on the list the new issue joins,
  // and an issue detail, where the stage host is not mounted, is left.
  const handleCreateIssue = (link: NavigationItemT) => {
    if (!link.issueTypeIri || !link.link) return
    setCurrIssueType(link.issueTypeIri)
    navigate(link.link)
    // Clear any stale draftIssueId so the new-issue form doesn't
    // inherit a previous draft's attachments / title (see IssueDialog
    // safety net + MobileFab for the matching pattern).
    setDraftIssueId(null)
    setKeepOpenOnNavigationTo(resolvePath(link.link, pathname).pathname)
    setDialogOpened(true)
    onOpenChange(false)
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* Subtle dim — enough to anchor attention on the sheet without
            turning the page dark. Linear uses an even lighter scrim. */}
        <DialogPrimitive.Overlay
          className={`fixed inset-0 z-[1040] bg-black/25 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:fill-mode-forwards`}
        />
        <DialogPrimitive.Content
          data-cy="bottom-tab-issues-sheet"
          onOpenAutoFocus={e => e.preventDefault()}
          // Always set: the exit keyframes only define `to`, so a dismissed sheet
          // slides out from where the finger left it.
          style={{ transform: `translateY(${drag.offset}px)` }}
          className={cn(
            // Anchor near the bottom, above the tab bar + safe area. The
            // `max-h` cap lets short lists auto-shrink and long lists scroll.
            `fixed inset-x-3 z-[1050] mx-auto flex max-w-md flex-col overflow-hidden rounded-2xl border bg-background shadow-2xl`,
            `bottom-[calc(var(--bottomTabBarHeight)+var(--safe-area-inset-bottom)+0.75rem)]`,
            `max-h-[70svh]`,
            `data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom data-[state=closed]:fill-mode-forwards`,
            !drag.dragging && `transition-transform duration-200`,
          )}
        >
          {/* Drag handle — iOS convention. Dragging here (not the list, which
              scrolls) pulls the sheet down; past the threshold it closes. */}
          <div
            data-cy="bottom-tab-issues-sheet-drag"
            className={`touch-none select-none`}
            onPointerDown={drag.onPointerDown}
            onPointerMove={drag.onPointerMove}
            onPointerUp={drag.onPointerUp}
            onPointerCancel={drag.onPointerCancel}
          >
            <div className={`flex justify-center pt-2`}>
              <div className={`h-1 w-10 rounded-full bg-muted-foreground/30`} />
            </div>

            <DialogPrimitive.Title className={`px-4 pb-2 pt-2 text-base font-semibold`}>
              {t('navigation.mobile.issues')}
            </DialogPrimitive.Title>
          </div>

          <div className={`flex flex-col gap-0.5 overflow-y-auto px-2 pb-3`}>
            {archivePath && (
              <NavLink
                to={archivePath}
                onClick={handleClose}
                className={`flex items-center gap-3 rounded-lg px-2 py-2.5 hover:bg-accent`}
              >
                <TablePropertiesIcon className={`size-4`} />
                <span className={`flex-1 text-sm font-medium`}>
                  {t('navigation.mobile.allIssues')}
                </span>
              </NavLink>
            )}

            {/* Static "Wg typu" section header — always expanded. The sheet
                is already ephemeral and short; an extra tap to reveal the
                type list doesn't earn its own affordance. */}
            <div
              className={`mt-1 px-2 py-1.5 text-[0.6875rem] font-medium uppercase tracking-wide text-muted-foreground`}
            >
              {t('navigation.mobile.byType')}
            </div>

            <div className={cn(`flex flex-col gap-0.5`)}>
              {links.length === 0 && (
                <span className={`px-2 py-2 text-sm text-muted-foreground`}>—</span>
              )}
              {links.map(link => {
                const issueType = link.issueTypeIri
                  ? issueTypes.find(t => t['@id'] === link.issueTypeIri)
                  : null
                const canCreate = !!issueType?.currentUserCanCreate && licenceAllowsCreate
                return (
                  <div
                    key={link.link ?? link.name}
                    className={`group flex items-center rounded-lg pr-1 hover:bg-accent`}
                  >
                    <NavLink
                      to={link.link ?? '#'}
                      onClick={handleClose}
                      className={`flex min-w-0 flex-1 items-center gap-3 px-2 py-2.5`}
                    >
                      <Dot className={`size-3.5`} fill={link.color} />
                      <span className={`flex-1 truncate text-sm`}>{link.name}</span>
                      {!!link.notifications && link.notifications > 0 && (
                        <Count quantity={link.notifications} variant="dark" className={`ml-0`} />
                      )}
                    </NavLink>
                    {canCreate && (
                      <button
                        type="button"
                        onClick={() => handleCreateIssue(link)}
                        aria-label={`${t('common.add')} ${link.name}`}
                        data-cy={`bottom-tab-issues-sheet-create-${issueType?.id ?? ''}`}
                        style={{ backgroundColor: link.color }}
                        className={`ml-2 flex size-7 shrink-0 items-center justify-center rounded-md text-white shadow-sm transition-transform active:scale-95`}
                      >
                        <Plus className={`size-4`} strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

// Distance (px) the sheet must be dragged down before release dismisses it.
export const SWIPE_DISMISS_THRESHOLD_PX = 60

function useSwipeDownToDismiss(open: boolean, onDismiss: () => void) {
  // The pointer driving the gesture; other fingers and buttons are ignored.
  const [gesture, setGesture] = useState<{ pointerId: number; startY: number } | null>(null)
  const [offset, setOffset] = useState(0)
  // A dismissed sheet keeps its offset through the exit animation; clear it
  // when the sheet opens again (render-time reset, no effect needed).
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setOffset(0)
  }

  const onPointerDown = (e: PointerEvent<HTMLElement>) => {
    if (gesture || !e.isPrimary || e.button !== 0) return
    setGesture({ pointerId: e.pointerId, startY: e.clientY })
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }
  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    if (gesture?.pointerId !== e.pointerId) return
    setOffset(Math.max(0, e.clientY - gesture.startY))
  }
  const onPointerUp = (e: PointerEvent<HTMLElement>) => {
    if (gesture?.pointerId !== e.pointerId) return
    setGesture(null)
    if (e.clientY - gesture.startY >= SWIPE_DISMISS_THRESHOLD_PX) onDismiss()
    // Short drag: the transition animates the sheet back up.
    else setOffset(0)
  }
  const onPointerCancel = (e: PointerEvent<HTMLElement>) => {
    if (gesture?.pointerId !== e.pointerId) return
    setGesture(null)
    setOffset(0)
  }

  return {
    offset,
    dragging: gesture !== null,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
  }
}
